import React, { useEffect, useRef, useState, createContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import History from "@/pages/History";
import Subscription from "@/pages/Subscription";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";
import AdminPanel from "@/pages/AdminPanel";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Use current origin for API calls so custom domains (e.g. aurexai.cloud) don't make cross-origin requests
export const API = `${window.location.origin}/api`;

// Auth Context
export const AuthContext = createContext(null);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = location.hash;
      console.log("AuthCallback: Processing hash", hash);
      const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");
      console.log("AuthCallback: Session ID", sessionId);

      if (!sessionId) {
        console.log("AuthCallback: No session ID found, redirecting to login");
        navigate("/login");
        return;
      }

      try {
        console.log("AuthCallback: Calling session API");
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId }
        );
        console.log("AuthCallback: API response", response.data);

        if (response.data.user) {
          // Store both token and user for authentication
          if (response.data.token) {
            localStorage.setItem("token", response.data.token);
            console.log("AuthCallback: Token stored");
          }
          localStorage.setItem("user", JSON.stringify(response.data.user));
          console.log("AuthCallback: User stored, navigating to dashboard");
          // Clear the hash from URL before navigating
          window.history.replaceState(null, '', '/dashboard');
          navigate("/dashboard", { state: { user: response.data.user }, replace: true });
        } else {
          console.log("AuthCallback: No user in response");
          navigate("/login");
        }
      } catch (error) {
        console.error("AuthCallback: Error", error.response?.data || error.message);
        navigate("/login");
      }
    };

    processSession();
  }, [location, navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Skip if user data passed from AuthCallback
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          
        });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        // Try localStorage token
        const storedUser = localStorage.getItem("user");
        const token = localStorage.getItem("token");
        
        if (storedUser && token) {
          try {
            const response = await axios.get(`${API}/auth/me`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            setUser(response.data);
            setIsAuthenticated(true);
          } catch {
            localStorage.removeItem("user");
            localStorage.removeItem("token");
            setIsAuthenticated(false);
            navigate("/login");
          }
        } else {
          setIsAuthenticated(false);
          navigate("/login");
        }
      }
    };

    checkAuth();
  }, [navigate, location]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user } = React.useContext(AuthContext);

  if (user?.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <History />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <Subscription />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription/success"
        element={
          <ProtectedRoute>
            <SubscriptionSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AdminPanel />
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
